// Transitions PR-1 — record the buyer's written Prepayment Notice on a
// payoff-family transition. The consequence copy is explicit (the
// TransitionStateControl TRANSITION_CONSEQUENCE pattern): the notice is
// IRREVOCABLE (§2.5), the binding payoff statement is persisted, and a missed
// tender is an immediate Event of Default (D-21 — the tender-lapse worker
// lands in PR-3).

import { useState } from "react";
import type { FormEvent } from "react";
import { Button, Label, Textarea } from "@/design-system/components/ui";
import { operationalToday } from "@/design-system/lib/format";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { useRecordPrepaymentNotice } from "@/hooks/portfolioAdmin/useLoanTransitions";

export function PrepaymentNoticeModal({
  loanId,
  transitionId,
  open,
  onClose,
}: {
  loanId: string;
  transitionId: string;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useRecordPrepaymentNotice(loanId);
  // `min` is this form's ONLY client-side floor — there is no `daysUntil` gate
  // here as there is in TransitionStartWizard — so reading it off the UTC day
  // silently refused the operator the current day from 17:00 Phoenix, on an
  // irrevocable notice. The server rule is `OperationalDay.Today()`
  // (`LoanTransitionService:321`); this is its mirror.
  const today = operationalToday();
  const [tenderDate, setTenderDate] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mutation.mutateAsync({
        transitionId,
        body: { tenderDate, note: note.trim() || null },
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record Prepayment Notice"
      description="Records the buyer's written notice of intent to pay the loan off in full, persists the binding payoff statement, and schedules the payoff."
    >
      <form onSubmit={submit} className="flex flex-col gap-space-3" data-testid="prepayment-notice-form">
        <div>
          <Label htmlFor="tender-date">Tender date</Label>
          <input
            id="tender-date"
            type="date"
            required
            min={today}
            className="rounded border border-border-light bg-white-pure px-space-2 py-space-1 text-small block"
            value={tenderDate}
            onChange={(e) => setTenderDate(e.target.value)}
            data-testid="tender-date-input"
          />
        </div>
        <div
          className="rounded border border-status-error/40 bg-status-error/5 p-space-2"
          data-testid="notice-consequence-warning"
        >
          <p className="text-small text-status-error font-semibold m-0">
            This notice is irrevocable under the note terms.
          </p>
          <p className="text-small text-gray-mid m-0 mt-space-1">
            If the payoff is not tendered by{" "}
            <strong>{tenderDate || "the tender date"}</strong>, the loan enters
            default automatically. Canceling after recording requires an audited
            staff override.
          </p>
        </div>
        <div>
          <Label htmlFor="notice-note">Note (optional)</Label>
          <Textarea
            id="notice-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="How the notice was delivered, reference numbers…"
          />
        </div>
        {error && <p className="text-small text-status-error m-0">{error}</p>}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending || !tenderDate} data-testid="record-notice-submit">
            {mutation.isPending ? "Recording…" : "Record irrevocable notice"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
